export { default as Component } from './ConfirmationPage';

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { apiClient } from '../../../lib/api-client';

const buildConfirmationPdf = async (data: Record<string, any>): Promise<jsPDF> => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const centerX = pageW / 2;

  // Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('TRANSPORT AUTOCAR', centerX, 22, { align: 'center' });
  doc.setFontSize(13);
  doc.text('by GERADA TUR', centerX, 30, { align: 'center' });
  doc.setFontSize(16);
  doc.text('Bilet', centerX, 40, { align: 'center' });

  // Route identification
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const route = data.statie_plecare && data.statie_destinatie
    ? `${data.statie_plecare} → ${data.statie_destinatie}`
    : '—';
  doc.text(route, centerX, 49, { align: 'center' });

  let dateStr = data.data_plecare ?? '';
  if (data.data_retur) dateStr += `  |  Întoarcere: ${data.data_retur}`;
  doc.text(dateStr, centerX, 55, { align: 'center' });

  // Separator
  doc.setDrawColor(200, 200, 200);
  doc.line(20, 59, pageW - 20, 59);

  // Details
  let y = 68;
  const writeLine = (label: string, value: string) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`${label}:`, 20, y);
    doc.setFont('helvetica', 'normal');
    doc.text(value || '—', 65, y);
    y += 7;
  };

  writeLine('Cod rezervare', data.cod_rezervare || '—');
  if (data.nr_pasageri) writeLine('Pasageri', String(data.nr_pasageri));
  if (data.categorie_bilet) writeLine('Categorie', data.categorie_bilet);
  if (data.pret_total != null) writeLine('Preț total', `${data.pret_total}€`);
  writeLine('Status', data.status === 'confirmed' ? 'Confirmat' : data.status === 'pending' ? 'În așteptare' : (data.status || '—'));

  // QR code
  if (data.cod_rezervare) {
    y += 6;
    const qrImage = await QRCode.toDataURL(data.cod_rezervare, { margin: 1, width: 256 });
    const qrSize = 45;
    const qrX = centerX - qrSize / 2;
    doc.addImage(qrImage, 'PNG', qrX, y, qrSize, qrSize);
    y += qrSize + 5;
    doc.setFontSize(8);
    doc.text(data.cod_rezervare, centerX, y, { align: 'center' });
  }

  return doc;
};

export default function ConfirmationPage() {
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState<any>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  useEffect(() => {
    const saved = sessionStorage.getItem('reservation_confirmation');
    if (!saved) { navigate('/'); return; }
    setConfirmation(JSON.parse(saved));
  }, [navigate]);

  // Optionally refetch via cod_rezervare for fresh data
  const codRezervare = confirmation?.cod_rezervare;
  const { data: freshData } = useQuery({
    queryKey: ['rezervare', codRezervare],
    queryFn: async () => {
      const payload = (await apiClient.get(`/rezervare/${codRezervare}`)).data;
      return payload?.data ?? payload;
    },
    enabled: !!codRezervare,
  });

  const data = freshData ?? confirmation;

  const handleDownloadPdf = useCallback(async () => {
    if (!data) return;
    setPdfGenerating(true);
    try {
      const doc = await buildConfirmationPdf(data);
      doc.save(`rezervare-${data.cod_rezervare || 'bilet'}.pdf`);
    } finally {
      setPdfGenerating(false);
    }
  }, [data]);

  if (!data) return null;

  const statusLabel = data.status === 'confirmed' ? 'Confirmată' :
    data.status === 'pending' ? 'În așteptare' :
    data.status === 'cancelled' ? 'Anulată' : data.status;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <div className="mb-6 rounded-lg border-2 border-green-300 bg-green-50 p-6 text-center" style={{ animation: 'fadeIn 300ms ease-out' }}>
        <div className="mb-2 text-4xl">✓</div>
        <h1 className="text-2xl font-bold text-green-800">Rezervare Confirmată!</h1>
        <p className="mt-2 text-green-700">Rezervarea ta a fost înregistrată cu succes.</p>
        <p className="mt-1 text-sm text-green-600">Un email de confirmare a fost trimis. Poți descărca biletul în format PDF mai jos.</p>
      </div>

      <div className="site-card rounded border p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold">Detalii Rezervare</h2>

        <dl className="space-y-3 text-sm">
          {data.cod_rezervare && (
            <div className="flex justify-between">
              <dt className="site-muted">Cod rezervare</dt>
              <dd className="font-mono font-bold text-lg">{data.cod_rezervare}</dd>
            </div>
          )}
          {data.statie_plecare && (
            <div className="flex justify-between">
              <dt className="site-muted">Traseu</dt>
              <dd>{data.statie_plecare} → {data.statie_destinatie}</dd>
            </div>
          )}
          {data.data_plecare && (
            <div className="flex justify-between">
              <dt className="site-muted">Data de plecare</dt>
              <dd>{data.data_plecare}</dd>
            </div>
          )}
          {data.data_retur && (
            <div className="flex justify-between">
              <dt className="site-muted">Data de întoarcere</dt>
              <dd>{data.data_retur}</dd>
            </div>
          )}
          {data.nr_pasageri && (
            <div className="flex justify-between">
              <dt className="site-muted">Număr pasageri</dt>
              <dd>{data.nr_pasageri}</dd>
            </div>
          )}
          {data.categorie_bilet && (
            <div className="flex justify-between">
              <dt className="site-muted">Categorie bilet</dt>
              <dd className="capitalize">{data.categorie_bilet}</dd>
            </div>
          )}
          {data.pret_total != null && (
            <div className="flex justify-between border-t pt-2">
              <dt className="font-medium">Preț total</dt>
              <dd className="text-lg font-bold text-blue-600">{data.pret_total}€</dd>
            </div>
          )}
          {data.status && (
            <div className="flex justify-between">
              <dt className="site-muted">Status</dt>
              <dd>
                <span className={`inline-block rounded px-2 py-0.5 text-xs ${
                  data.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                  data.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-700'
                }`}>{statusLabel}</span>
              </dd>
            </div>
          )}
        </dl>

        {data.payment_url && (
          <a href={data.payment_url} className="mt-4 block rounded bg-green-600 px-6 py-3 text-center text-white hover:bg-green-700 transition-colors">
            Plătește acum
          </a>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button
          onClick={handleDownloadPdf}
          disabled={pdfGenerating}
          className="site-btn-primary rounded px-6 py-2 text-sm text-white disabled:opacity-50 transition-opacity"
        >
          {pdfGenerating ? 'Se generează...' : '📥 Descarcă bilet PDF'}
        </button>
        <button onClick={() => window.print()} className="site-soft rounded border px-6 py-2 text-sm">
          🖨️ Printează
        </button>
        <Link to="/" className="site-btn-primary rounded px-6 py-2 text-sm text-white">
          Rezervare nouă
        </Link>
      </div>
    </div>
  );
}
